import React, {FC, useContext, isValidElement, Fragment, ReactNode} from 'react';
import {LocalizerContext} from './LocalizerContext';
import {logWarning} from './localizerUtils';

export type InterpolationInjectValue = string | ReactNode | ((content: string | undefined, label: string) => ReactNode);

interface InterpolationProps {
	l10nCode: string;

	/**
	 * Map of inserted values.
	 * It can be either a simple string or a function that returns a component.
	 */
	[key: string]: InterpolationInjectValue;
}

/**
 * Usage examples:
 * 	<Interpolation
 * 		l10nCode='sizeError'
 * 		maxSize='10'
 * 	/>
 *
 * 	<Interpolation
 * 		l10nCode='terms'
 * 		link={(key, content) => <a href='/'>{content}</a>)}
 * 	/>
 */
export const Interpolation: FC<InterpolationProps> = props => {
	const context = useContext(LocalizerContext);
	if (!context) {
		throw new Error('Interpolation exception: localizer context is not provided');
	}

	const {l10nCode: code, ...inserts} = props;
	const parts = context.translations[code];

	if (parts === undefined) {
		logWarning(`string ${code} is not found`);
		// Workaround for issue: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/20544
		return <>[{code}]</>;
	}

	// Return as a plain string when the source string is not interpolation
	if (parts.type !== 'interpolation') {
		logWarning(`${code} is not interpolation string`);
		return <>{parts}</>;
	}

	return (
		<>
			{parts.parts.map((part, index) => {
				if (!(index % 2)) {
					return part;
				}

				const {label, content} = part as {label: string; content?: string};
				const insert = inserts[label];

				if (!insert) {
					return content;
				}

				if (typeof insert === 'string') {
					return insert;
				}

				if (isValidElement(insert)) {
					return <Fragment key={label + index.toString()}>{insert}</Fragment>;
				}

				if (typeof insert === 'function') {
					return <Fragment key={label + index.toString()}>{insert(content, label)}</Fragment>;
				}

				return '';
			})}
		</>
	);
};

Interpolation.displayName = 'Interpolation';
